"use client";

import { useEffect, useRef, useState } from "react";

const peaksCache = new Map<string, number[]>();
const inflight = new Map<string, Promise<number[] | null>>();

async function decodePeaks(url: string, barCount: number): Promise<number[] | null> {
  const cacheKey = `${url}::${barCount}`;
  const cached = peaksCache.get(cacheKey);
  if (cached) return cached;

  const existing = inflight.get(cacheKey);
  if (existing) return existing;

  const promise = (async (): Promise<number[] | null> => {
    try {
      const response = await fetch(url, { mode: "cors" });
      if (!response.ok) return null;
      const arrayBuffer = await response.arrayBuffer();

      const Ctor =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return null;
      const ctx = new Ctor();
      const audioBuffer = await ctx.decodeAudioData(arrayBuffer.slice(0));
      ctx.close();

      const channelData = audioBuffer.getChannelData(0);
      const sampleSize = Math.max(1, Math.floor(channelData.length / barCount));
      const peaks: number[] = [];
      let maxAmplitude = 0;

      for (let i = 0; i < barCount; i++) {
        const start = i * sampleSize;
        const end = Math.min(start + sampleSize, channelData.length);
        let peak = 0;
        for (let j = start; j < end; j++) {
          const value = Math.abs(channelData[j] ?? 0);
          if (value > peak) peak = value;
        }
        peaks.push(peak);
        if (peak > maxAmplitude) maxAmplitude = peak;
      }

      // Normalize to 0-100 with a sensible floor so quiet sounds still render
      const normalized = peaks.map(p => {
        if (maxAmplitude === 0) return 20;
        const v = (p / maxAmplitude) * 100;
        return Math.max(8, Math.round(v));
      });

      peaksCache.set(cacheKey, normalized);
      return normalized;
    } catch {
      return null;
    } finally {
      inflight.delete(cacheKey);
    }
  })();

  inflight.set(cacheKey, promise);
  return promise;
}

export interface UseAudioPeaksResult {
  peaks: number[] | null;
  loading: boolean;
}

/**
 * Computes real waveform peaks from an audio URL using AudioContext.
 * Returns cached peaks instantly if previously decoded.
 * Pass null/undefined url to skip decoding.
 */
export function useAudioPeaks(
  url: string | null | undefined,
  barCount: number = 48,
  enabled: boolean = true
): UseAudioPeaksResult {
  const initialKey = url ? `${url}::${barCount}` : null;
  const initial = initialKey ? (peaksCache.get(initialKey) ?? null) : null;
  const [peaks, setPeaks] = useState<number[] | null>(initial);
  const [loading, setLoading] = useState<boolean>(!initial && Boolean(url) && enabled);
  const lastUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !url) {
      // Intentional synchronous reset when hook is toggled off or URL is cleared.
      /* eslint-disable react-hooks/set-state-in-effect, react-doctor/no-cascading-set-state */
      setPeaks(null);
      setLoading(false);
      /* eslint-enable react-hooks/set-state-in-effect, react-doctor/no-cascading-set-state */
      return;
    }
    const key = `${url}::${barCount}`;
    if (lastUrlRef.current === key) return;
    lastUrlRef.current = key;

    const cached = peaksCache.get(key);
    if (cached) {
      setPeaks(cached);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    decodePeaks(url, barCount).then(result => {
      if (cancelled) return;
      setPeaks(result);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [url, barCount, enabled]);

  return { peaks, loading };
}
